import React, { useState, useEffect, useMemo, useRef } from'react';
import { 
 Mail, Save, RotateCcw, ChevronDown, ChevronUp, 
 Loader2, Info, AlertCircle, Eye, Code, Upload, Image, Send
} from'lucide-react';
import { Button } from'../ui/button';
import { Badge } from'../ui/badge';
import { toast } from'sonner';
import { API_URL } from'../../utils/api';
import ReactQuill from'react-quill-new';
import'react-quill-new/dist/quill.snow.css';
import { TestEmailDialog } from'./weekender';

// Custom CSS to ensure Quill editor looks good
const quillStyles = `
 .email-template-editor .ql-container {
 font-family: -apple-system, BlinkMacSystemFont,'Segoe UI', Roboto, sans-serif;
 font-size: 14px;
 min-height: 120px;
 background: white;
 border-radius: 0 0 6px 6px;
 }
 .email-template-editor .ql-toolbar {
 border-radius: 6px 6px 0 0;
 background: #f8fafc;
 }
 .email-template-editor .ql-editor {
 min-height: 100px;
 }
 .email-template-editor.dark .ql-container {
 background: #1e293b;
 color: #e2e8f0;
 }
 .email-template-editor.dark .ql-toolbar {
 background: #334155;
 border-color: #475569;
 }
 .email-template-editor.dark .ql-stroke {
 stroke: #e2e8f0;
 }
 .email-template-editor.dark .ql-fill {
 fill: #e2e8f0;
 }
 .email-template-editor.dark .ql-picker-label {
 color: #e2e8f0;
 }
`;

export function AdminEmailTemplatesTab() {
 const [templates, setTemplates] = useState([]);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [expandedTemplate, setExpandedTemplate] = useState(null);
 const [editedSections, setEditedSections] = useState({});
 const [viewMode, setViewMode] = useState('wysiwyg'); //'wysiwyg' or'code'
 
 // Logo state
 const [logoUrl, setLogoUrl] = useState('');
 const [isCustomLogo, setIsCustomLogo] = useState(false);
 const [uploadingLogo, setUploadingLogo] = useState(false);
 const fileInputRef = useRef(null);
 
 // Test Email Dialog state
 const [showTestEmailDialog, setShowTestEmailDialog] = useState(false);

 // Quill editor modules configuration
 const quillModules = useMemo(() => ({
 toolbar: [
 ['bold','italic','underline'],
 [{'list':'ordered'}, {'list':'bullet' }],
 ['link'],
 ['clean']
 ]
 }), []);

 const quillFormats = [
'bold','italic','underline',
'list',
'link'
 ];

 useEffect(() => {
 fetchTemplates();
 fetchLogo();
 }, []);

 const fetchLogo = async () => {
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/admin/email-templates/logo`, {
 headers: {'Authorization': `Bearer ${token}` }
 });
 
 if (response.ok) {
 const data = await response.json();
 setLogoUrl(data.logo_url ||'');
 setIsCustomLogo(data.is_custom || false);
 }
 } catch (error) {
 console.error('Error fetching logo:', error);
 }
 };

 const handleLogoUpload = async (event) => {
 const file = event.target.files?.[0];
 if (!file) return;
 
 // Validate file type
 if (!file.type.startsWith('image/')) {
 toast.error('Please select an image file');
 return;
 }
 
 // Validate file size (2MB)
 if (file.size > 2 * 1024 * 1024) {
 toast.error('Image must be less than 2MB');
 return;
 }
 
 setUploadingLogo(true);
 try {
 const token = localStorage.getItem('token');
 const formData = new FormData();
 formData.append('file', file);
 
 const response = await fetch(`${API_URL}/api/admin/email-templates/logo/upload`, {
 method:'POST',
 headers: {'Authorization': `Bearer ${token}` },
 body: formData
 });
 
 if (response.ok) {
 const data = await response.json();
 setLogoUrl(data.logo_url);
 setIsCustomLogo(true);
 toast.success('Logo uploaded successfully');
 } else {
 const error = await response.json();
 toast.error(error.detail ||'Failed to upload logo');
 }
 } catch (error) {
 toast.error('Error uploading logo');
 } finally {
 setUploadingLogo(false);
 // Reset file input
 if (fileInputRef.current) {
 fileInputRef.current.value ='';
 }
 }
 };

 const handleLogoUrlSave = async () => {
 if (!logoUrl.trim()) {
 toast.error('Please enter a logo URL');
 return;
 }
 
 setSaving(true);
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/admin/email-templates/logo`, {
 method:'PUT',
 headers: {
'Content-Type':'application/json',
'Authorization': `Bearer ${token}`
 },
 body: JSON.stringify({ logo_url: logoUrl.trim() })
 });
 
 if (response.ok) {
 setIsCustomLogo(true);
 toast.success('Logo URL saved successfully');
 } else {
 const error = await response.json();
 toast.error(error.detail ||'Failed to save logo URL');
 }
 } catch (error) {
 toast.error('Error saving logo URL');
 } finally {
 setSaving(false);
 }
 };

 const handleLogoReset = async () => {
 if (!window.confirm('Reset logo to default? This cannot be undone.')) {
 return;
 }
 
 setSaving(true);
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/admin/email-templates/logo/reset`, {
 method:'POST',
 headers: {'Authorization': `Bearer ${token}` }
 });
 
 if (response.ok) {
 const data = await response.json();
 setLogoUrl(data.logo_url);
 setIsCustomLogo(false);
 toast.success('Logo reset to default');
 } else {
 toast.error('Failed to reset logo');
 }
 } catch (error) {
 toast.error('Error resetting logo');
 } finally {
 setSaving(false);
 }
 };

 const fetchTemplates = async () => {
 setLoading(true);
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/admin/email-templates`, {
 headers: {'Authorization': `Bearer ${token}` }
 });
 
 if (response.ok) {
 const data = await response.json();
 setTemplates(data.templates || []);
 } else {
 toast.error('Failed to load email templates');
 }
 } catch (error) {
 console.error('Error fetching templates:', error);
 toast.error('Error loading email templates');
 } finally {
 setLoading(false);
 }
 };

 const handleSectionChange = (templateKey, sectionKey, value) => {
 setEditedSections(prev => ({
 ...prev,
 [templateKey]: {
 ...prev[templateKey],
 [sectionKey]: value
 }
 }));
 };

 const handleSave = async (templateKey) => {
 const sections = editedSections[templateKey];
 if (!sections || Object.keys(sections).length === 0) {
 toast.info('No changes to save');
 return;
 }

 setSaving(true);
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/admin/email-templates/${templateKey}`, {
 method:'PUT',
 headers: {
'Content-Type':'application/json',
'Authorization': `Bearer ${token}`
 },
 body: JSON.stringify({ sections })
 });

 if (response.ok) {
 toast.success('Template saved successfully');
 // Clear edited sections for this template
 setEditedSections(prev => {
 const newState = { ...prev };
 delete newState[templateKey];
 return newState;
 });
 // Refresh templates
 fetchTemplates();
 } else {
 const error = await response.json();
 toast.error(error.detail ||'Failed to save template');
 }
 } catch (error) {
 toast.error('Error saving template');
 } finally {
 setSaving(false);
 }
 };

 const handleReset = async (templateKey, templateName) => {
 if (!window.confirm(`Reset"${templateName}" to default settings? This cannot be undone.`)) {
 return;
 }

 setSaving(true);
 try {
 const token = localStorage.getItem('token');
 const response = await fetch(`${API_URL}/api/admin/email-templates/${templateKey}/reset`, {
 method:'POST',
 headers: {'Authorization': `Bearer ${token}` }
 });

 if (response.ok) {
 toast.success('Template reset to defaults');
 setEditedSections(prev => {
 const newState = { ...prev };
 delete newState[templateKey];
 return newState;
 });
 fetchTemplates();
 } else {
 toast.error('Failed to reset template');
 }
 } catch (error) {
 toast.error('Error resetting template');
 } finally {
 setSaving(false);
 }
 };

 const hasChanges = (templateKey) => {
 return editedSections[templateKey] && Object.keys(editedSections[templateKey]).length > 0;
 };

 const getSectionValue = (template, sectionKey) => {
 // Check if there's an edited value first
 if (editedSections[template.template_key]?.[sectionKey] !== undefined) {
 return editedSections[template.template_key][sectionKey];
 }
 // Otherwise return the current saved value
 return template.sections[sectionKey]?.content ||'';
 };

 // Determine if a section should use WYSIWYG or plain text
 const isRichTextSection = (sectionKey) => {
 // Bank details and contact info are better as plain text for exact formatting
 const plainTextSections = ['bank_details','contact_info'];
 return !plainTextSections.includes(sectionKey);
 };

 if (loading) {
 return (
 <div className="flex items-center justify-center py-12">
 <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
 </div>
 );
 }

 return (
 <div className="space-y-6" data-testid="email-templates-tab">
 {/* Inject custom styles for Quill */}
 <style>{quillStyles}</style>
 
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
 <Mail className="w-5 h-5" />
 Email Templates
 </h2>
 <p className="text-sm text-slate-500 mt-1">
 Customize the content of automated emails sent to customers
 </p>
 </div>
 
 <div className="flex items-center gap-3">
 {/* Test Email Button */}
 <Button
 variant="outline"
 onClick={() => setShowTestEmailDialog(true)}
 className="gap-2"
 data-testid="global-test-email-btn"
 >
 <Send className="w-4 h-4" />
 Test Email
 </Button>
 
 {/* View Mode Toggle */}
 <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
 <Button
 variant={viewMode ==='wysiwyg' ?'default' :'ghost'}
 size="sm"
 onClick={() => setViewMode('wysiwyg')}
 className={viewMode ==='wysiwyg' ?'bg-white shadow-sm' :''}
 >
 <Eye className="w-4 h-4 mr-1" />
 Visual
 </Button>
 <Button
 variant={viewMode ==='code' ?'default' :'ghost'}
 size="sm"
 onClick={() => setViewMode('code')}
 className={viewMode ==='code' ?'bg-white shadow-sm' :''}
 >
 <Code className="w-4 h-4 mr-1" />
 Code
 </Button>
 </div>
 </div>
 </div>

 {/* Info Banner */}
 <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
 <div className="flex gap-3">
 <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
 <div className="text-sm text-blue-800">
 <p className="font-medium mb-1">How it works:</p>
 <ul className="list-disc list-inside space-y-1 text-blue-700">
 <li>Edit the sections below to customize your email content</li>
 <li>Use <code className="bg-blue-100 px-1 rounded">{'{{variable_name}}'}</code> to insert dynamic values (e.g., <code className="bg-blue-100 px-1 rounded">{'{{booking_deadline}}'}</code>)</li>
 <li>Changes only affect the editable sections - the email structure remains intact</li>
 <li>Use"Reset to Defaults" to restore original content</li>
 </ul>
 </div>
 </div>
 </div>

 {/* Email Logo Section */}
 <div className="bg-white border border-slate-200 rounded-xl p-5">
 <div className="flex items-center gap-3 mb-4">
 <Image className="w-5 h-5 text-violet-600" />
 <div>
 <h3 className="font-medium text-slate-900">Email Logo</h3>
 <p className="text-sm text-slate-500">This logo appears in the header of all emails</p>
 </div>
 </div>
 
 <div className="flex flex-col md:flex-row gap-6 items-start">
 {/* Logo Preview */}
 <div className="flex-shrink-0">
 <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center border-4 border-white shadow-lg">
 {logoUrl ? (
 <img 
 src={logoUrl} 
 alt="Email Logo" 
 className="w-full h-full object-cover"
 onError={(e) => {
 e.target.style.display ='none';
 }}
 />
 ) : (
 <span className="text-white text-2xl font-bold">LDC</span>
 )}
 </div>
 {isCustomLogo && (
 <Badge className="mt-2 bg-green-100 text-green-700">Custom Logo</Badge>
 )}
 </div>
 
 {/* Logo Controls */}
 <div className="flex-1 space-y-4">
 {/* Upload Button */}
 <div>
 <input
 type="file"
 ref={fileInputRef}
 onChange={handleLogoUpload}
 accept="image/*"
 className="hidden"
 />
 <Button
 variant="outline"
 onClick={() => fileInputRef.current?.click()}
 disabled={uploadingLogo}
 className="w-full md:w-auto"
 >
 {uploadingLogo ? (
 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
 ) : (
 <Upload className="w-4 h-4 mr-2" />
 )}
 Upload New Logo
 </Button>
 <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 2MB. Square images work best.</p>
 </div>
 
 {/* Or use URL */}
 <div className="flex items-center gap-2">
 <span className="text-xs text-slate-500 uppercase">or</span>
 <div className="flex-1 h-px bg-slate-200"></div>
 </div>
 
 {/* URL Input */}
 <div className="flex gap-2">
 <input
 type="url"
 value={logoUrl}
 onChange={(e) => setLogoUrl(e.target.value)}
 placeholder="https://example.com/logo.png"
 className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500"
 />
 <Button
 size="sm"
 onClick={handleLogoUrlSave}
 disabled={saving}
 className="bg-violet-600 hover:bg-violet-700"
 >
 <Save className="w-4 h-4" />
 </Button>
 </div>
 
 {/* Reset Button */}
 {isCustomLogo && (
 <Button
 variant="outline"
 size="sm"
 onClick={handleLogoReset}
 disabled={saving}
 className="text-slate-600"
 >
 <RotateCcw className="w-4 h-4 mr-2" />
 Reset to Default
 </Button>
 )}
 </div>
 </div>
 </div>

 {/* Templates List */}
 <div className="space-y-4">
 {templates.map((template) => (
 <div 
 key={template.template_key}
 className="bg-white border border-slate-200 rounded-xl overflow-hidden"
 data-testid={`template-${template.template_key}`}
 >
 {/* Template Header */}
 <div 
 className="px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
 onClick={() => setExpandedTemplate(
 expandedTemplate === template.template_key ? null : template.template_key
 )}
 >
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <Mail className="w-5 h-5 text-violet-600" />
 <div>
 <h3 className="font-medium text-slate-900">
 {template.name}
 </h3>
 <p className="text-sm text-slate-500">{template.description}</p>
 </div>
 </div>
 <div className="flex items-center gap-3">
 {hasChanges(template.template_key) && (
 <Badge className="bg-amber-100 text-amber-700">Unsaved Changes</Badge>
 )}
 {template.last_updated && (
 <span className="text-xs text-slate-500">
 Last updated: {new Date(template.last_updated).toLocaleDateString()}
 </span>
 )}
 {expandedTemplate === template.template_key ? (
 <ChevronUp className="w-5 h-5 text-slate-500" />
 ) : (
 <ChevronDown className="w-5 h-5 text-slate-500" />
 )}
 </div>
 </div>
 </div>

 {/* Expanded Content */}
 {expandedTemplate === template.template_key && (
 <div className="px-5 pb-5 border-t border-slate-200">
 <div className="pt-4 space-y-5">
 {/* Available Variables */}
 <div className="bg-slate-50 rounded-lg p-3">
 <p className="text-xs font-medium text-slate-600 mb-2">
 Available Variables:
 </p>
 <div className="flex flex-wrap gap-1">
 {template.variables?.map(variable => (
 <code 
 key={variable}
 className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded cursor-pointer hover:bg-violet-100 hover:text-violet-700"
 onClick={() => {
 navigator.clipboard.writeText(`{{${variable}}}`);
 toast.success(`Copied {{${variable}}} to clipboard`);
 }}
 title="Click to copy"
 >
 {`{{${variable}}}`}
 </code>
 ))}
 </div>
 </div>

 {/* Editable Sections */}
 {Object.entries(template.sections).map(([sectionKey, section]) => (
 <div key={sectionKey} className="space-y-2">
 <div className="flex items-center justify-between">
 <label className="text-sm font-medium text-slate-700">
 {section.label}
 </label>
 {section.is_customized && (
 <Badge variant="outline" className="text-xs">Customized</Badge>
 )}
 </div>
 
 {/* Render WYSIWYG for message sections, plain textarea for bank/contact */}
 {viewMode ==='wysiwyg' && isRichTextSection(sectionKey) ? (
 <div className="email-template-editor">
 <ReactQuill
 theme="snow"
 value={getSectionValue(template, sectionKey)}
 onChange={(value) => handleSectionChange(template.template_key, sectionKey, value)}
 modules={quillModules}
 formats={quillFormats}
 placeholder={section.default_content}
 />
 </div>
 ) : (
 <textarea
 value={getSectionValue(template, sectionKey)}
 onChange={(e) => handleSectionChange(template.template_key, sectionKey, e.target.value)}
 rows={sectionKey ==='bank_details' || sectionKey ==='contact_info' ? 5 : 3}
 className="w-full font-mono text-sm bg-slate-50 border border-slate-200 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-violet-500"
 placeholder={section.default_content}
 />
 )}
 
 {section.default_content && (
 <p className="text-xs text-slate-500">
 Default: <span className="font-mono">{section.default_content.substring(0, 80)}{section.default_content.length > 80 ?'...' :''}</span>
 </p>
 )}
 </div>
 ))}

 {/* Action Buttons */}
 <div className="flex items-center justify-between pt-4 border-t border-slate-200">
 <Button
 variant="outline"
 size="sm"
 onClick={() => handleReset(template.template_key, template.name)}
 disabled={saving}
 className="text-slate-600"
 >
 <RotateCcw className="w-4 h-4 mr-2" />
 Reset to Defaults
 </Button>
 <Button
 size="sm"
 onClick={() => handleSave(template.template_key)}
 disabled={saving || !hasChanges(template.template_key)}
 className="bg-violet-600 hover:bg-violet-700"
 >
 {saving ? (
 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
 ) : (
 <Save className="w-4 h-4 mr-2" />
 )}
 Save Changes
 </Button>
 </div>
 </div>
 </div>
 )}
 </div>
 ))}
 </div>

 {templates.length === 0 && !loading && (
 <div className="text-center py-12 text-slate-500">
 <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
 <p>No email templates found</p>
 </div>
 )}
 
 {/* Test Email Dialog */}
 <TestEmailDialog
 open={showTestEmailDialog}
 onClose={() => setShowTestEmailDialog(false)}
 authToken={localStorage.getItem('token')}
 />
 </div>
 );
}
